import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { Collapsible } from '@openx/components-core/lib/Collapsible/Collapsible';
import { useEffect, useMemo, useState } from 'react';

import type { TopMarketRequestsType } from '../../../constants';
import { StyledContainer } from '../../../styledShared/StyledContainer';
import { StyledInfoIcon } from '../../../styledShared/StyledInfoIcon';
import { StyledTableCell } from '../../../styledShared/StyledTableCell';
import { StyledTableRow } from '../../../styledShared/StyledTableRow';
import type { TopMarketRequestsData } from '../../../types';
import { getMarketRequestsTextValues } from '../../../utils/getMarketRequestsTextValues';

type TopMarketRequestsTableProps = {
  type: TopMarketRequestsType;
  tableData: TopMarketRequestsData[];
};

export const TopMarketRequestsTable = ({ type, tableData }: TopMarketRequestsTableProps) => {
  const textValues = useMemo(() => getMarketRequestsTextValues(type), [type]);

  const valueColumnHeadingId = `${type}-value-column-text`;
  const [valueColumnWidth, setValueColumnWidth] = useState<number | undefined>();

  useEffect(() => {
    const heading = document.getElementById(valueColumnHeadingId);

    if (heading?.clientWidth) {
      setValueColumnWidth(heading.clientWidth);
    }
  }, [valueColumnHeadingId]);

  if (!tableData?.length || !textValues) {
    return null;
  }

  return (
    <StyledContainer applyBackgroundColor data-test={`top-${type}-container`}>
      <Collapsible
        titleRender={
          <Typography variant="h3" data-test="top-market-requests-title">
            {textValues.sectionTitle}
          </Typography>
        }
      >
        <TableContainer sx={{ maxHeight: '185px', overflowX: 'auto', width: '100%' }} data-test={`top-${type}-table`}>
          <Table stickyHeader>
            <TableHead sx={theme => ({ borderBottom: `1px solid ${theme.palette.background.default}` })}>
              <StyledTableRow>
                <StyledTableCell
                  width={'auto'}
                  data-test="top-market-requests-table-title"
                  sx={theme => ({
                    backgroundColor: theme.palette.background.paper,
                    padding: `0 0 ${theme.spacing(1)}`,
                  })}
                >
                  {textValues.tableTitle}{' '}
                  <Tooltip
                    title={textValues.tooltipText}
                    placement="top"
                    data-test={`top-market-requests-tooltip-${type}`}
                  >
                    <StyledInfoIcon />
                  </Tooltip>
                </StyledTableCell>

                <StyledTableCell
                  width={valueColumnWidth ? `${valueColumnWidth}px` : undefined}
                  sx={theme => ({
                    backgroundColor: theme.palette.background.paper,
                    padding: `0 0 ${theme.spacing(1)}`,
                  })}
                >
                  <Box id={valueColumnHeadingId} style={{ width: 'max-content' }}>
                    {textValues.valuesColumnTitle}
                  </Box>
                </StyledTableCell>
              </StyledTableRow>
            </TableHead>

            <TableBody data-test="table-body">
              {tableData.map(({ marketRequests, progress, entry }, index) => {
                const firstRowStyles = index === 0 ? { paddingTop: theme => theme.spacing(1) } : {};

                return (
                  <StyledTableRow key={index} noBorder data-test="table-row">
                    <StyledTableCell width={'auto'} denseCells data-test="table-cell">
                      <Box
                        sx={{
                          ...firstRowStyles,
                          alignItems: 'center',
                          display: 'flex',
                          width: '100%',
                        }}
                      >
                        <Box
                          sx={{
                            display: 'inline-block',
                            marginRight: '6px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            width: '43%',
                            maxWidth: '150px',
                          }}
                        >
                          <Tooltip placement="top" title={entry}>
                            <Box component={'span'}>{entry}</Box>
                          </Tooltip>
                        </Box>

                        <Box sx={{ display: 'inline-block', width: '57%' }}>
                          <LinearProgress
                            data-test="progress-bar"
                            variant="determinate"
                            value={progress}
                            sx={{
                              '& > .MuiLinearProgress-bar': {
                                backgroundColor: 'charts.indigo',
                              },
                              backgroundColor: 'transparent',
                              height: '10px',
                            }}
                          />
                        </Box>
                      </Box>
                    </StyledTableCell>

                    <StyledTableCell
                      width={valueColumnWidth ? `${valueColumnWidth}px` : undefined}
                      sx={{
                        ...firstRowStyles,
                        textAlign: 'center',
                      }}
                      denseCells
                      data-test="table-cell"
                    >
                      {marketRequests}
                    </StyledTableCell>
                  </StyledTableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Collapsible>
    </StyledContainer>
  );
};
